"use client";

/**
 * Staff — visibility into the admissions review pipeline, and the levers that
 * keep it moving.
 *
 * Staff never evaluate or vote: they pass `require_committee_viewer` (GET
 * routes) but not `require_committee_voting_member`, so the checklist and the
 * vote 403 for them. Since 2026-10-29 (Joel's direction) they may **move a
 * review to the Board** once the vote floor is met — the outcome is still the
 * majority of votes cast, so they move the committee's decision, not make one —
 * and **re-send** an outcome ChartRoom did not acknowledge.
 *
 * The Board tab is the Board's own queue. Staff read it; only the named
 * approvers decide, and `BoardQueuePanel` works that out for itself.
 *
 * Voting is open (C-1), so the running tally shows here while a review is still
 * being voted on — staff supporting the committee historically kept it on a
 * spreadsheet anyway. What a staff viewer does NOT get is how any individual
 * member voted: `review_detail` withholds per-voter directions from anyone
 * holding no assignment on the committee, and that distinction is the backend's
 * to make, not this component's.
 *
 * This is a NEW staff section (`admissions_review`). The older `admissions`
 * section and its `AdmissionsPrototype` demo are untouched.
 *
 * The one place staff write here is the **Awaiting applicant** tab: sending an
 * applicant's reply to a committee request on to the committee. It decides
 * nothing — see `admissions/AwaitingApplicantTab.tsx`.
 */

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, Bell, RefreshCw } from "lucide-react";
import {
  closeReview,
  getBoardQueue,
  getReview,
  listReviews,
  listStaffInfoRequests,
  remindVoters,
  retryReport,
  type BoardQueueRow,
  type ReviewDetail,
  type ReviewQueue,
  type StaffInfoRequestGroup,
} from "@/lib/admissions-committee/api";
import BoardQueuePanel from "@/components/admissions-board/BoardQueuePanel";
import AwaitingApplicantTab from "./admissions/AwaitingApplicantTab";

const chip = "text-[9.5px] font-bold tracking-wide rounded px-1.5 py-0.5";
const label = "text-[10px] font-black uppercase tracking-widest text-gray-500";

type Tab = "committee" | "applicant" | "board";

/** `?tab=applicant&review=…` — where the staff follow-up email links to. */
const fromUrl = () => {
  if (typeof window === "undefined") return { tab: null, review: null };
  const params = new URLSearchParams(window.location.search);
  const tab = params.get("tab");
  return {
    tab: (tab === "applicant" || tab === "board" || tab === "committee" ? tab : null) as Tab | null,
    review: params.get("review"),
  };
};

/** The committee's majority as a past-tense verb, for "… by the committee". */
const COMMITTEE_VERB: Record<string, string> = {
  approve: "Approved",
  deny: "Denied",
  defer: "Deferred",
};

/** Who moved a closed review to the Board, for the tally line. */
const MOVED_BY: Record<string, string> = {
  chair: "moved to the Board by the chair",
  liaison: "moved to the Board by the staff liaison",
  staff: "moved to the Board by staff",
  auto: "moved to the Board automatically a day after the majority",
};

function StatusChip({ row }: { row: ReviewQueue["awaiting"][number] }) {
  if (row.status === "closed") {
    // Where the close put it in ChartRoom: every close is with the Board until
    // the Board decides.
    if (row.board_outcome)
      return <span className={`${chip} bg-[#003d45]/10 capitalize text-[#003d45]`}>Board: {row.board_outcome}</span>;
    if (row.routed_to === "board_review")
      return <span className={`${chip} bg-[#003d45]/10 text-[#003d45]`}>With the Board</span>;
    return <span className={`${chip} bg-gray-100 capitalize text-gray-700`}>{row.outcome ?? "closed"}</span>;
  }
  if (row.awaiting_applicant)
    return <span className={`${chip} bg-amber-100 text-amber-800`}>Awaiting applicant</span>;
  return row.checklist_status === "final" ? (
    <span className={`${chip} bg-[#007a8a]/10 text-[#007a8a]`}>Voting open</span>
  ) : (
    <span className={`${chip} bg-amber-100 text-amber-800`}>Checklist draft</span>
  );
}

export function AdmissionsReviewConsolePanel() {
  const [tab, setTab] = useState<Tab>(() => fromUrl().tab ?? "committee");
  const [focusReview] = useState<string | null>(() => fromUrl().review);
  const [queue, setQueue] = useState<ReviewQueue | null>(null);
  const [requests, setRequests] = useState<{ waiting: StaffInfoRequestGroup[]; resolved: StaffInfoRequestGroup[] } | null>(null);
  const [board, setBoard] = useState<{ awaiting: BoardQueueRow[]; ratified: BoardQueueRow[] } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reminding, setReminding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveReason, setMoveReason] = useState("");

  const load = useCallback(() => {
    setError(null);
    listReviews().then(setQueue).catch((e: Error) => setError(e.message));
    listStaffInfoRequests().then(setRequests).catch(() => setRequests({ waiting: [], resolved: [] }));
    // Staff read the Board queue; only the count is needed here, the tab
    // renders the Board's own panel.
    getBoardQueue().then(setBoard).catch(() => setBoard(null));
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(load, [load]);

  useEffect(() => {
    if (!selected) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNotice(null);
    setMoving(false);
    setMoveReason("");
    getReview(selected).then(setDetail).catch((e: Error) => setError(e.message));
  }, [selected]);

  // ChartRoom is told after the response; poll while the send is in flight.
  const sending = detail?.report?.status === "sending";
  useEffect(() => {
    if (!selected || !sending) return;
    const timer = setInterval(() => {
      getReview(selected).then(setDetail).catch(() => undefined);
    }, 5000);
    return () => clearInterval(timer);
  }, [selected, sending]);

  if (!queue) {
    return <p className="text-sm text-gray-500">{error ?? "Loading…"}</p>;
  }

  const all = [...queue.awaiting_applicant, ...queue.awaiting, ...queue.completed];
  const outstanding = (detail?.roster ?? []).filter((v) => !v.has_voted);

  // The outcome a move would record: the majority of votes cast, else a
  // deferral — the backend's `majority_outcome`, mirrored for the confirm copy.
  const t = detail?.tally;
  const pending: string | null = !t || t.total === 0
    ? null
    : (["approve", "deny", "defer"] as const).find((v) => t[v] * 2 > t.total) ?? "defer";
  const reasonNeeded = pending === "deny" || pending === "defer";

  /** Run a write against the open review, then refresh it and the lists. */
  const act = (run: () => Promise<unknown>, done: string) => {
    if (!detail) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    run()
      .then(() => getReview(detail.id))
      .then((d) => {
        setDetail(d);
        setNotice(done);
        setMoving(false);
        setMoveReason("");
        load();
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  };

  return (
    <div id="admissions-review-body" className="space-y-5">
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-xl font-black text-[#003d45]">Admissions Review</h2>
        </div>
        <p className="mt-1 text-[13px] text-gray-500">
          What the Admissions Committee and the Board are working on. The checklist and the vote belong to
          the committee. Once at least two members have voted you can move a review to the Board; it moves
          on its own a day after a majority of the votes cast agree. When the committee asks an applicant
          for something, it lands in <strong>Awaiting applicant</strong> for you to send on.
        </p>
      </header>

      {error && (
        <p className="flex items-start gap-2 rounded-xl bg-[#ae232d]/5 p-3 text-[13px] text-[#ae232d]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {error}
        </p>
      )}

      <nav className="flex flex-wrap gap-1">
        {([
          ["committee", `Committee (${all.length})`],
          ["applicant", `Awaiting applicant (${requests ? requests.waiting.length : "—"})`],
          ["board", `Board (${board ? board.awaiting.length : "—"})`],
        ] as Array<[Tab, string]>).map(
          ([id, text]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`rounded-lg px-3 py-1.5 text-[13px] font-bold transition ${
                tab === id ? "bg-[#003d45] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {text}
            </button>
          ),
        )}
      </nav>

      {tab === "committee" && (
        <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
          <aside>
            <p className={label}>All reviews ({all.length})</p>
            {all.length === 0 && (
              <p className="mt-2 text-[13px] text-gray-400">
                No applications have been referred to the committee yet.
              </p>
            )}
            <ul className="mt-2 space-y-1">
              {all.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setSelected(r.id)}
                    className={`w-full rounded-xl border px-3 py-2 text-left text-sm transition ${
                      selected === r.id ? "border-[#007a8a] bg-[#007a8a]/5" : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <span className="block font-bold text-[#1a1a1a]">
                      {r.applicant_name || `Application #${r.application_id}`}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-1">
                      <StatusChip row={r} />
                      <span className="text-[11px] text-gray-400">
                        {r.progress.votes_cast}/{r.progress.roster_size} voted
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section className="min-w-0">
            {!detail && <p className="text-sm text-gray-500">Select a review.</p>}
            {detail && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-black text-[#003d45]">
                    {detail.applicant_name || `Application #${detail.application_id}`}
                  </h3>
                  <p className="mt-0.5 text-[13px] text-gray-500">
                    {detail.progress.votes_cast} of {detail.progress.roster_size} committee members
                    have voted
                    {detail.progress.outstanding > 0 && ` · ${detail.progress.outstanding} outstanding`}
                  </p>
                </div>

                <div className="rounded-xl border border-gray-200 p-4">
                  <p className={label}>Checklist</p>
                  <p className="mt-1 text-sm text-[#1a1a1a]">
                    {detail.checklist.status === "final"
                      ? `Finalised — Section F decision: ${detail.checklist.decision ?? "—"}`
                      : detail.checklist.status === "draft"
                        ? "In progress — voting is still locked."
                        : "Not started."}
                  </p>
                </div>

                {(detail.info_requests ?? []).length > 0 && (
                  <div className="rounded-xl border border-gray-200 p-4">
                    <p className={label}>Asked of the applicant</p>
                    <ul className="mt-1 space-y-1.5">
                      {(detail.info_requests ?? []).map((r) => (
                        <li key={r.id} className="text-[13px] text-[#1a1a1a]">
                          <span
                            className={`${chip} mr-1.5 ${
                              r.status === "open"
                                ? "bg-amber-100 text-amber-800"
                                : r.status === "answered"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {r.status === "open" ? "Awaiting applicant" : r.status === "answered" ? "Answered" : "No response"}
                          </span>
                          {r.ask}
                        </li>
                      ))}
                    </ul>
                    {(detail.info_requests ?? []).some((r) => r.status === "open") && (
                      <button
                        type="button"
                        onClick={() => setTab("applicant")}
                        className="mt-2 text-[12px] font-bold text-[#007a8a] hover:underline"
                      >
                        Send the applicant&rsquo;s reply on →
                      </button>
                    )}
                  </div>
                )}

                <div className="rounded-xl border border-gray-200 p-4">
                  <p className={label}>Eligible voters ({detail.roster.length})</p>
                  <ul className="mt-1 flex flex-wrap gap-1.5">
                    {detail.roster.map((v) => (
                      <li key={v.user_id ?? v.display_name} className={`${chip} bg-gray-100 text-gray-700`}>
                        {v.display_name}
                        {v.is_chair && " · chair"}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[12px] text-gray-400">
                    Who is eligible is not confidential; how each of them voted is.
                  </p>

                  {/* The one write staff hold here, and only because it decides
                      nothing: a reminder asks a member to do their own job. It
                      is a backstop until chairs own the habit. */}
                  {detail.status !== "closed" && outstanding.length > 0 && (
                    <div className="mt-3 border-t border-gray-100 pt-3">
                      <p className="text-[12px] text-gray-600">
                        <span className="font-bold">{outstanding.length}</span> still to vote:{" "}
                        {outstanding.map((v) => v.display_name).join(", ")}
                      </p>
                      <button
                        disabled={reminding}
                        onClick={() => {
                          setReminding(true);
                          setError(null);
                          remindVoters(detail.id)
                            .then((r) => {
                              // Name who was held back and why. "Nothing sent"
                              // alone reads as a failure, and a staff member
                              // will click again rather than wait out a
                              // cooldown they were never told about.
                              const held = r.skipped.map((s) => s.name).join(", ");
                              if (!r.sent.length) {
                                setNotice(
                                  held
                                    ? `Nothing sent — ${held} ${r.skipped.length === 1 ? "was" : "were"} reminded within the last few days.`
                                    : "Everyone outstanding was reminded recently — nothing sent.",
                                );
                                return;
                              }
                              setNotice(
                                `Reminder sent to ${r.sent.join(", ")}.` +
                                  (held ? ` Skipped ${held} — reminded recently.` : ""),
                              );
                            })
                            .catch((e: Error) => setError(e.message))
                            .finally(() => setReminding(false));
                        }}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#007a8a] px-3 py-1.5 text-[12px] font-bold text-white disabled:opacity-50"
                      >
                        <Bell size={12} /> {reminding ? "Sending…" : "Send reminder"}
                      </button>
                      {notice && <p className="mt-1.5 text-[12px] text-emerald-700">{notice}</p>}
                    </div>
                  )}
                </div>

                {detail.status === "closed" && detail.tally ? (
                  <div className="rounded-xl border border-gray-200 p-4">
                    <p className={label}>Outcome</p>
                    <p className="mt-1 text-lg font-black capitalize text-[#003d45]">{detail.outcome}</p>
                    <p className="text-[13px] text-gray-600">{detail.outcome_rationale}</p>
                    <p className="mt-2 text-[13px] text-gray-500">
                      Approve {detail.tally.approve} · Deny {detail.tally.deny} · Defer{" "}
                      {detail.tally.defer}
                      {detail.closed_capacity
                        ? ` · ${MOVED_BY[detail.closed_capacity] ?? "closed"}`
                        : detail.closed_early && " · closed before everyone voted"}
                    </p>
                    <p className="mt-2 text-[13px] text-gray-700">
                      {detail.board_decision?.outcome
                        ? `Sent to the Board of Directors, who decided: ${detail.board_decision.outcome}.`
                        : detail.checklist.decision === "flag_board"
                          ? "Flagged for the Board in Section F — with the Board of Directors for the final decision."
                          : `${COMMITTEE_VERB[detail.outcome ?? ""] ?? "Closed"} by the committee — with the Board of Directors for the final decision.`}
                    </p>
                    {detail.report?.status === "failed" ? (
                      <div className="mt-2 rounded-lg bg-amber-50 p-2 text-[13px] text-amber-900">
                        <p>
                          Decided, but ChartRoom hasn&rsquo;t confirmed it yet: {detail.report.detail}
                        </p>
                        <p className="mt-1 text-[12px] text-amber-800">
                          {detail.report.detail?.startsWith("ChartRoom returned 409")
                            ? "ChartRoom says the application isn't waiting for a committee decision — it may already have this one. Check it in ChartRoom before retrying."
                            : "The Hub retries automatically every 30 minutes. The Board can't decide it until ChartRoom confirms."}
                        </p>
                        {detail.viewer.can_retry_report && (
                          <button
                            disabled={busy}
                            onClick={() => act(() => retryReport(detail.id), "Re-sending to ChartRoom…")}
                            className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-amber-200 px-2.5 py-1 text-[12px] font-bold text-amber-900 disabled:opacity-50"
                          >
                            <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Retry now
                          </button>
                        )}
                      </div>
                    ) : null}
                    {notice && <p className="mt-1.5 text-[12px] text-emerald-700">{notice}</p>}
                    {sending ? (
                      <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-gray-500">
                        <RefreshCw size={12} className="animate-spin" /> Sending to ChartRoom… this
                        can take a minute or two.
                      </p>
                    ) : null}
                    {detail.report?.status === "ok" ? (
                      <p className="mt-2 text-[12px] text-gray-400">
                        Sent to ChartRoom — {detail.report.detail}
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-xl bg-gray-50 p-3 text-[13px] text-gray-600">
                    <p>
                      Voting is still open
                      {detail.progress &&
                        ` — ${detail.progress.votes_cast} of ${detail.progress.roster_size} have voted`}
                      .
                    </p>
                    {detail.tally && (
                      <p className="mt-1 text-gray-500">
                        Approve {detail.tally.approve} · Deny {detail.tally.deny} · Defer{" "}
                        {detail.tally.defer}
                      </p>
                    )}
                    <p className="mt-1 text-[12px] text-gray-400">
                      You see the running tally; how each member voted is the committee&rsquo;s own.
                    </p>

                    {detail.viewer.can_close && pending && (
                      <div className="mt-3 border-t border-gray-200 pt-3">
                        {!detail.close.allowed ? (
                          <p className="text-[12px] text-gray-500">{detail.close.reason}</p>
                        ) : !moving ? (
                          <button
                            onClick={() => setMoving(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#003d45] px-3 py-1.5 text-[12px] font-bold text-white"
                          >
                            Send to the Board <ArrowRight size={12} />
                          </button>
                        ) : (
                          <div className="rounded-lg bg-white p-3">
                            <p className="text-[13px] text-[#1a1a1a]">
                              This closes the committee&rsquo;s review as{" "}
                              <strong className="capitalize">{pending}</strong>, the majority of the{" "}
                              {t?.total} votes cast, and sends it to the Board for the final decision.
                              Members who haven&rsquo;t voted can&rsquo;t vote afterwards.
                            </p>
                            <textarea
                              rows={2}
                              value={moveReason}
                              onChange={(e) => setMoveReason(e.target.value)}
                              placeholder={
                                reasonNeeded
                                  ? "Reason for the record (required for a denial or deferral)"
                                  : "Note for the record (optional)"
                              }
                              className="mt-2 w-full rounded-lg border border-gray-300 px-2 py-1 text-[13px]"
                            />
                            <div className="mt-2 flex gap-2">
                              <button
                                disabled={busy || (reasonNeeded && !moveReason.trim())}
                                onClick={() =>
                                  act(() => closeReview(detail.id, moveReason.trim()), "Moved to the Board.")
                                }
                                className="rounded-lg bg-[#007a8a] px-3 py-1.5 text-[12px] font-bold text-white disabled:opacity-50"
                              >
                                {busy ? "Moving…" : "Confirm — send to the Board"}
                              </button>
                              <button
                                disabled={busy}
                                onClick={() => setMoving(false)}
                                className="rounded-lg bg-gray-200 px-3 py-1.5 text-[12px] font-bold text-gray-800"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      )}

      {tab === "applicant" && (
        <AwaitingApplicantTab
          waiting={requests?.waiting ?? []}
          resolved={requests?.resolved ?? []}
          focusReview={focusReview}
          onChanged={load}
        />
      )}

      {tab === "board" && <BoardQueuePanel />}
    </div>
  );
}
